import { useMemo } from 'react';

import { ILanguagesCodes } from '@picsart/rc/languages';
import { languagesCodesListFull } from 'constants/languages';
import { useLocation } from 'react-router-dom';
import trim from 'utils/trim';
import { PageLocationParameters } from './types';

/**
 * Returns a list of location parameters
 *  example: https://github.com/es/someRoute/tree/master?branch=release&source=gitlab
 *  returned values are {
 *    slug: 'someRoute/tree/master',
 *    slugWithQueryParams: 'someRoute/tree/master?branch=release&source=gitlab',
 *    queryParams: '?branch=release&source=gitlab',
 *    languageParam: 'es',
 *    fullPath: 'es/someRoute/tree/master?branch=release&source=gitlab'
 *  }
 * @returns {PageLocationParameters} slug, slugWithQueryParams, queryParams, languageParam, fullPath
 */
function usePageSlug(): PageLocationParameters {
  const { pathname, search } = useLocation();

  const removeLanguageCodeFromRoute = useMemo(
    () => route => {
      const locationDividedBySlash = route.split('/');
      let validLocation = route ?? '';

      if (['/', ''].includes(locationDividedBySlash?.[0])) {
        locationDividedBySlash?.shift();
      }

      // check if language code is included in the location
      if (languagesCodesListFull.includes(locationDividedBySlash[0] as ILanguagesCodes)) {
        locationDividedBySlash?.shift();
        validLocation = locationDividedBySlash?.join('/') ?? '';
        return removeLanguageCodeFromRoute(validLocation);
      }
      validLocation = locationDividedBySlash?.join('/') ?? '';

      return validLocation;
    },
    [],
  );

  // Get slug without lang and query params
  const getSlug = useMemo(
    () => () => {
      const validLocation = removeLanguageCodeFromRoute(pathname);
      return trim(validLocation, '/');
    },
    [pathname, removeLanguageCodeFromRoute],
  );

  // get language parameter in the url
  const getLanguageParam = useMemo(
    () => () => languagesCodesListFull.find(x => x === pathname?.split('/')?.[1]) ?? '',
    [pathname],
  );

  // Get slug with query params without lang
  const getSlugWithQueryParams = useMemo(
    () => () => `${getSlug()}${search ?? ''}`,
    [getSlug, search],
  );

  // Get full url inclusive slug, query params and lang
  const getFullPath = useMemo(() => () => `${pathname}${search ?? ''}`, [pathname, search]);

  return {
    slug: getSlug(),
    slugWithQueryParams: getSlugWithQueryParams(),
    queryParams: search ?? '',
    languageParam: getLanguageParam(),
    fullPath: getFullPath(),
  };
}

export default usePageSlug;
